"use client";

import { CldUploadButton, CloudinaryUploadWidgetInfo } from "next-cloudinary";
import { FC, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import {
  Document,
  DocumentCategory,
  DocumentSource,
} from "@/prisma-package/src";
import {
  createNewDocumentAction,
  extractDocumentSuggestionsAction,
  extractDocumentTextAction,
  searchOrgDocumentsAction,
  searchProjectDocumentsAction,
} from "@/app/_lib/actions/document.actions";
import { useToast } from "@/app/_lib/hooks/use-toast";
import CloudImage from "./cloud-image";
import {
  DocumentTypeDetection,
  scoreDocumentType,
} from "@/app/_lib/utils/document-type-detection.utils";
import {
  detectionFieldsForSave,
  detectionForPending,
} from "@/app/_lib/utils/upload-detection.utils";
import { useDocumentTypeDefinitions } from "@/app/_lib/hooks/use-document-type-definitions";
import {
  resolveUploadCategory,
  categoryLabel,
  documentTypeLabel,
} from "@/app/_lib/utils/document-category.utils";
import { MdAdd } from "react-icons/md";
import {
  DocumentVerificationTable,
  PendingUploadDocument,
} from "../documents/document-verification-table";
import { Vendor } from "@/prisma-package/src";

type Props = {
  callToAction: string;
  allowMultiples?: boolean;
  maxFiles?: number;
  createdBy: string;
  organizationId?: string;
  documentType?: string;
  category?: DocumentCategory;
  source?: DocumentSource;
  tags?: string[];
  onSuccess?: (doc: Document | Document[] | null) => void;
  buttonStyle?: "button" | "link";
  projectId?: string;
  vendors?: Vendor[];
  showLinkExisting?: boolean;
};

const ImageUploader: FC<Props> = ({
  callToAction,
  allowMultiples = false,
  maxFiles,
  organizationId,
  createdBy,
  documentType,
  category,
  source,
  tags,
  onSuccess,
  buttonStyle = "button",
  projectId,
  vendors = [],
  showLinkExisting = true,
}) => {
  const toast = useToast();
  const { definitions } = useDocumentTypeDefinitions(projectId);
  const modal = useRef<HTMLDialogElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [queryResults, setQueryResults] = useState<Document[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<PendingUploadDocument[]>([]);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const uploadedCount = useRef(0);
  const totalToUpload = useRef(0);
  const pendingUploads = useRef(0);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationLoadingMessage, setVerificationLoadingMessage] = useState("");

  useEffect(() => {
    if (searchTerm === "" && projectId) {
      (async () => {
        const [projectDocumentsError, projectDocuments] =
          await searchProjectDocumentsAction({
            projectId,
            query: "",
            documentType,
          });

        setQueryResults(() => {
          if (!projectDocuments || projectDocumentsError) return [];
          return projectDocuments;
        });
      })();
    }
  }, [setQueryResults, documentType, projectId, searchTerm]);

  useEffect(() => {
    if (!!searchTerm && projectId) {
      const search = setTimeout(async () => {
        const [projectDocumentsError, projectDocuments] =
          await searchProjectDocumentsAction({
            projectId,
            query: searchTerm,
            documentType,
          });

        setQueryResults(() => {
          if (!projectDocuments || projectDocumentsError) return [];
          return projectDocuments;
        });
      }, 3000);

      return () => clearTimeout(search);
    }
  }, [setQueryResults, documentType, projectId, searchTerm]);

  const handleUploadComplete = () => {
    setShowVerificationModal(false);
    setPendingDocuments([]);
    uploadedCount.current = 0;
    totalToUpload.current = 0;
    pendingUploads.current = 0;
    setVerificationLoading(false);
    setVerificationLoadingMessage("");
    modal.current?.close();
  };

  return (
    <>
      <div className="flex items-center gap-4">
        <CldUploadButton
          className={classNames("btn btn-primary", {
            "btn-link py-2": buttonStyle === "link",
            "btn btn-md": buttonStyle === "button",
          })}
          uploadPreset={process.env.NEXT_PUBLIC_CLOUDINARY_UPLOAD_PRESET}
          onClose={() => {
            // User clicked Done in the Cloudinary widget
            if (projectId && allowMultiples && totalToUpload.current > 0) {
              // Show the verification modal now that Cloudinary widget is closed
              setShowVerificationModal(true);
            }
          }}
          onQueuesEnd={() => {
            // Queue has ended, but uploads might still be processing
            setIsUploading(false);
          }}
          onQueuesStart={(info) => {
            if (projectId && allowMultiples) {
              // Track total files to upload
              const queueLength = (info as any)?.queue?.length || 1;
              totalToUpload.current = queueLength;
              uploadedCount.current = 0;
              pendingUploads.current = totalToUpload.current;
              setIsUploading(true);
              setPendingDocuments([]);

              // Prepare loading state but don't show modal yet
              if (queueLength > 0) {
                setVerificationLoading(true);
                setVerificationLoadingMessage("Uploading documents...");
              }
            }
          }}
          onSuccess={async (results) => {
            const info = results.info as CloudinaryUploadWidgetInfo & {
              info?: {
                ocr?: {
                  adv_ocr?: {
                    data?: { fullTextAnnotation?: { text?: string } }[];
                  };
                };
              };
            };
            const ocr = info?.info?.ocr;
            let documentText =
              ocr?.adv_ocr?.data?.[0]?.fullTextAnnotation?.text ?? "";

            // No OCR text (preset has OCR disabled, or the add-on quota is
            // exhausted): digital PDFs carry an embedded text layer, so
            // extract it server-side instead. Scans come back empty.
            if (!documentText && info.format === "pdf") {
              const [extractError, extracted] =
                await extractDocumentTextAction({
                  secureUrl: info.secure_url,
                });
              if (!extractError && extracted) {
                documentText = extracted.documentText;
              }
            }

            // When the type is auto-detected (no explicit documentType), keep
            // the full detection so the verification table can show how well
            // each document was recognized, and warn if the type doesn't fit
            // the tab. The stored type is still constrained by the action.
            let detection: DocumentTypeDetection | null = null;
            let detectedType = documentType;
            if (!detectedType) {
              detection = scoreDocumentType(
                documentText,
                definitions,
                info.original_filename
              );
              detectedType = detection.type;
            }

            // The detected type routes the document to the category it belongs
            // to, instead of the tab coercing the type: a W9 uploaded from the
            // Project Expenses tab is saved to Vendor Compliance as a W9.
            const routedCategory = resolveUploadCategory(detectedType, category, {
              hasProjectId: !!projectId,
              defs: definitions,
            });

            if (projectId && allowMultiples) {
              // DEFERRED-SAVE PATH: don't persist yet. Collect the uploaded file
              // as a pending document, fetch AI suggestions for the verification
              // modal, and only create it in the DB when the user confirms. This
              // way cancelling the modal discards the uploads entirely.
              try {
                const pending: PendingUploadDocument = {
                  publicId: info.public_id,
                  secureUrl: info.secure_url,
                  width: info.width,
                  height: info.height,
                  documentText,
                  detectedType,
                  detection: detection ? detectionForPending(detection) : null,
                  uploadedAt: Date.now(),
                  extractionStatus: "pending",
                  suggestedDocumentId: null,
                  suggestedVendor: null,
                  vendorCandidates: [],
                  suggestedAlternates: [],
                  suggestedFields: {},
                };

                setPendingDocuments((prev) => [...prev, pending]);
                uploadedCount.current += 1;
                pendingUploads.current -= 1;

                if (pendingUploads.current > 0) {
                  setVerificationLoadingMessage(
                    `Processing documents... (${uploadedCount.current}/${totalToUpload.current})`,
                  );
                } else {
                  setVerificationLoadingMessage("Extracting data from documents...");
                  // Uploads are done; give extraction a beat then reveal the modal.
                  setTimeout(() => setVerificationLoading(false), 1200);
                }

                // Fetch AI vendor/document-id suggestions (no persistence).
                const [suggestionsError, suggestions] =
                  await extractDocumentSuggestionsAction({
                    secureUrl: info.secure_url,
                    documentText,
                    type: detectedType,
                    category: routedCategory,
                    projectId,
                  });

                if (suggestionsError) {
                  console.error(
                    "[doc-extraction] suggestions failed for",
                    info.public_id,
                    suggestionsError
                  );
                } else {
                  console.log(
                    `[doc-extraction] suggestions for ${info.public_id}: type=${detectedType} documentId=${suggestions?.extractedDocumentId ?? "null"} vendorMatches=${suggestions?.vendorMatches?.length ?? 0}`
                  );
                }

                const topMatch =
                  !suggestionsError && suggestions
                    ? suggestions.vendorMatches?.[0]
                    : undefined;
                // Mark extraction settled either way so the verification table
                // stops showing its loading state for this row.
                setPendingDocuments((prev) =>
                  prev.map((p) =>
                    p.publicId === pending.publicId
                      ? {
                          ...p,
                          extractionStatus: "done",
                          ...(!suggestionsError && suggestions
                            ? {
                                suggestedDocumentId:
                                  suggestions.extractedDocumentId ?? null,
                                suggestedVendor: topMatch
                                  ? {
                                      id: topMatch.vendorId,
                                      name: topMatch.vendorName,
                                      confidence: topMatch.confidence,
                                    }
                                  : null,
                                vendorCandidates: suggestions.vendorCandidates ?? [],
                                suggestedAlternates: (
                                  suggestions.alternateVendors ?? []
                                ).map((alt) => ({
                                  id: alt.vendorId,
                                  name: alt.vendorName,
                                  confidence: alt.confidence,
                                })),
                                suggestedFields:
                                  suggestions.extractedFields ?? {},
                              }
                            : {}),
                        }
                      : p,
                  ),
                );
              } catch (err: unknown) {
                // The upload itself may have succeeded; make sure the row
                // doesn't spin forever if the suggestion step blew up.
                setPendingDocuments((prev) =>
                  prev.map((p) =>
                    p.publicId === info.public_id
                      ? { ...p, extractionStatus: "done" }
                      : p,
                  ),
                );
                toast({
                  message: (err as Error).message,
                  type: "error",
                  state: "active",
                });
                pendingUploads.current -= 1;
                if (pendingUploads.current <= 0) {
                  setVerificationLoading(false);
                }
              }
              return;
            }

            // SINGLE-UPLOAD PATH: persist immediately (no verification modal).
            try {
              const [newDocumentError, newDocument] =
                await createNewDocumentAction({
                  publicId: info.public_id,
                  secureUrl: info.secure_url,
                  type: detectedType,
                  ...detectionFieldsForSave(
                    detection ? detectionForPending(detection) : null,
                    detection ? detectedType : null,
                    detectedType,
                  ),
                  category: routedCategory,
                  source,
                  width: info.width,
                  height: info.height,
                  documentText,
                  projectId,
                });

              if (newDocumentError) {
                throw new Error(newDocumentError.message);
              }

              if (newDocument) {
                if (onSuccess) {
                  onSuccess(newDocument);
                }

                // Tell the user when the document was routed somewhere other
                // than the tab they uploaded from.
                if (category && routedCategory && routedCategory !== category) {
                  toast({
                    message: `This looks like ${documentTypeLabel(detectedType, definitions)}, so it was saved to ${categoryLabel(routedCategory)}.`,
                    type: "info",
                    state: "active",
                  });
                } else {
                  toast({
                    message: "Upload complete!",
                    type: "success",
                    state: "active",
                  });
                }
              }
            } catch (err: unknown) {
              toast({
                message: (err as Error).message,
                type: "error",
                state: "active",
              });
            }
          }}
          onError={(error) => {
            console.error("Cloudinary upload error:", error);
            toast({
              message: `Upload failed: ${typeof error === "string" ? error : JSON.stringify(error)}`,
              type: "error",
              state: "active",
            });
            
            if (projectId && allowMultiples) {
              pendingUploads.current -= 1;
              
              // Check if all other uploads are complete
              if (pendingUploads.current === 0 && uploadedCount.current > 0) {
                // Switch to extracting data message
                setVerificationLoadingMessage("Extracting data from documents...");
                
                // After a brief delay, show vendors fetching message then turn off loading
                setTimeout(() => {
                  setVerificationLoadingMessage("Fetching vendors...");
                  setTimeout(() => {
                    setVerificationLoading(false);
                  }, 1000);
                }, 1000);
              }
            }
          }}
          options={{
            sources: ["local", "google_drive", "dropbox", "url"],
            multiple: allowMultiples,
            maxFiles,
            tags,
            context: {
              organizationId,
              createdBy,
              documentType,
              projectId,
            },
            showPoweredBy: false,
          }}
        >
          <MdAdd />
          {callToAction}
        </CldUploadButton>
        {onSuccess && showLinkExisting && (
          <button
            type="button"
            className={classNames("btn btn-primary", {
              "btn-link py-2": buttonStyle === "link",
              "btn btn-md btn-outline": buttonStyle === "button",
            })}
            onClick={() => modal.current?.showModal()}
          >
            Link Existing Document
          </button>
        )}
      </div>
      {onSuccess && showLinkExisting && (
        <dialog ref={modal} className="modal">
          <div className="modal-box w-11/12 max-w-5xl">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
              <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
                ✕
              </button>
            </form>
            <h3 className="font-bold text-lg mb-4">Choose Document</h3>
            <input
              type="text"
              value={searchTerm}
              className="border border-1 border-cnd-purple p-2 w-full mb-4 rounded-md"
              placeholder="Search for a document..."
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {queryResults.map((doc) => (
                <button
                  type="button"
                  className="border-4 border-white hover:border-cnd-purple"
                  key={doc.publicId}
                  onClick={() => {
                    onSuccess?.(doc);
                  }}
                >
                  <CloudImage
                    width={400}
                    height={400}
                    src={doc.publicId}
                    alt={doc.publicId}
                    documentType={doc.type}
                  />
                </button>
              ))}
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button>close</button>
          </form>
        </dialog>
      )}

      {/* Document Verification Table */}
      {projectId && organizationId && (
        <DocumentVerificationTable
          pendingDocuments={pendingDocuments}
          projectId={projectId}
          organizationId={organizationId}
          vendors={vendors}
          uploadCategory={category}
          isOpen={showVerificationModal}
          isLoading={verificationLoading}
          loadingMessage={verificationLoadingMessage}
          onClose={() => {
            // Cancel/close discards the pending uploads — nothing was persisted.
            handleUploadComplete();
          }}
          onComplete={(createdDocuments) => {
            handleUploadComplete();
            // The documents were just created; surface them to the parent list.
            if (onSuccess && createdDocuments.length > 0) {
              // Pass all documents for bulk upload, single document for single upload
              onSuccess(createdDocuments.length === 1 ? createdDocuments[0] : createdDocuments);
            }
          }}
        />
      )}
    </>
  );
};

export default ImageUploader;